import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { INVITE_PROFESSIONALS_TO_JOBS, inviteProfessionalsToJobs } from '~/actions';
import {
  factoryGetInvitableOnsiteInterviewsKey,
  IGetInvitableOnsiteInterviewsResult,
} from '~/domain/PermanentJobs';
import { useIsSuccess, useLoading } from '~/hooks';

interface IUseInviteToInterviewProps {
  officeId: string;
  postingId: string;
  professionalId: string;
}

export const useInviteToInterview = ({
  officeId,
  postingId,
  professionalId,
}: IUseInviteToInterviewProps) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const pendingInviteIdRef = useRef<string | null>(null);

  const { isLoading } = useLoading([INVITE_PROFESSIONALS_TO_JOBS]);
  const { isSuccess, clearSuccess } = useIsSuccess([INVITE_PROFESSIONALS_TO_JOBS]);

  useEffect(() => {
    if (!isSuccess || !pendingInviteIdRef.current) return;

    const id = pendingInviteIdRef.current;
    queryClient.setQueryData<IGetInvitableOnsiteInterviewsResult>(
      factoryGetInvitableOnsiteInterviewsKey({ postingId, professionalId }),
      (old) => {
        if (!old) return old;
        return {
          ...old,
          data: old.data.map((interview) =>
            interview.onsiteInterviewId === id ? { ...interview, isInvited: true } : interview,
          ),
        };
      },
    );

    clearSuccess();
    pendingInviteIdRef.current = null;
  }, [isSuccess, postingId, professionalId, queryClient, clearSuccess]);

  const handleInvite = (id: string) => {
    pendingInviteIdRef.current = id;
    dispatch(
      inviteProfessionalsToJobs({
        jobIds: [id],
        professionalIds: [professionalId],
        isJobsInvite: false,
        officeId,
      }),
    );
  };

  return {
    isLoading,
    handleInvite,
  };
};
